import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import CustomerSidebar from "@/pages/sidebar/customer-sidebar";
import CustomerHeader from "./header";
import { convertThousandSeparator } from "@/scripts/functions";
import {
  CalendarDays,
  UserCircle,
  CreditCard,
  Building2,
  Plus,
} from "lucide-react";
import { capitalizeFirstLetter } from "@/utils/string-manipulation";
import { useProfile } from "@/hooks/profile/use-profile";
import {
  addresseeEmail,
  addresseeNamePriority,
  addresseePhone,
  canInvoiceBePaid,
} from "@/utils/invoice";
import { formatPhoneNumber } from "@/utils/functions";
import usePaymentMethods from "@/hooks/payment/use-payment-methods";
import { useAuth } from "@/providers/auth";
import { Box, Divider, Modal } from "@mui/material";
import { Button, textStyles } from "@/ui/";
import { Button as ButtonTw } from "@/components/ui/button-tw";
import styled from "styled-components";
import { toast } from "react-toastify";
import api from "@/api/v1-jwt/config/api";
import { Plaid } from "@/components/payment-methods";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { getInvoiceById } from "@/api/v1-jwt/invoice/invoice";
import { cn } from "@/utils/classnames";
import { InitiateLinkAccount } from "@/components/payment-methods/plaid-link-card";
import { useGetPaymentMethods } from "@/hooks/payment/use-get-payment-methods";
import {
  setDeluxePaymentToken,
  getDeluxeSurcharge,
} from "@/api/v1-jwt/payment-methods/payment-methods";
import EmbeddedPaymentForm from "@/components/embedded-payment-form";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 480,
  bgcolor: "background.paper",
  boxShadow: 24,
  padding: "24px",
  borderRadius: "15px",
};

const CustomerInvoiceDetail = () => {
  const history = useHistory();
  const { data: profile } = useProfile();
  const { selectedInvoice } = useSelector((state) => state.page);
  const [shortCut, setShortCut] = useState("");
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [currentWidth, setCurrentWidth] = useState(0);

  useEffect(() => {
    if (selectedInvoice) {
      setInvoiceDetail(selectedInvoice);
    } else {
      history.push("/invoices");
    }
  }, [selectedInvoice]);

  const handlePayInvoice = () => {
    history.push(`/invoices/pay/${selectedInvoice.invoice_id}`);
  };

  return (
    <PayPalScriptProvider
      options={{
        clientId:
          "AZhsLNS4XKr-xBomen5OSvNQXTkxOkc50Eu7pfWUKCBKGgy0iehII0C6JP_C-Evu06-ne-YNUB7P-w6e",
        disableFunding: "credit",
      }}
    >
      <div className="h-100">
        <CustomerSidebar
          currentWidth={currentWidth}
          setCurrentWidth={setCurrentWidth}
        />
        <div className="py-[20px] px-4 lg:py-8 lg:px-10 xl:py-12 xl:px-[50px] h-[calc(100% - 101px)] lg:h-full lg:ml-[315px] overflow-auto bg-[#f8f8f8] relative">
          <CustomerHeader
            title="Invoice"
            shortCut={shortCut}
            setShortCut={setShortCut}
          />

          <div className="mx-auto grid max-w-2xl grid-cols-1 grid-rows-1 items-start gap-x-8 gap-y-8 lg:mx-0 lg:max-w-none lg:grid-cols-3 my-8">
            <div className="lg:col-start-3 lg:row-end-1">
              <InvoiceSummary
                invoice={invoiceDetail}
                handlePay={handlePayInvoice}
              />
            </div>

            <div className="-mx-4 px-4 py-8 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:rounded-lg sm:px-8 sm:pb-14 lg:col-span-2 lg:row-span-2 lg:row-end-2 xl:px-16 xl:pb-20 xl:pt-16">
              <h2 className="text-base font-semibold leading-6 text-gray-900">
                Invoice
              </h2>
              <dl className="mt-6 grid grid-cols-1 text-sm leading-6 sm:grid-cols-2">
                <div>
                  <div className="sm:pr-4">
                    <dt className="inline text-gray-500">Sent on</dt>{" "}
                    <dd className="inline text-gray-700">
                      <time>
                        {invoiceDetail?.invoice_info.invoice_send_date
                          ? moment(
                              invoiceDetail?.invoice_info.invoice_send_date,
                            ).format("LL")
                          : null}
                      </time>
                    </dd>
                  </div>
                  <div className="mt-2 sm:mt-0 ">
                    <dt className="inline text-gray-500">Due on</dt>{" "}
                    <dd className="inline text-gray-700">
                      <time>
                        {invoiceDetail?.invoice_info.invoice_due_date
                          ? moment(
                              invoiceDetail?.invoice_info.invoice_due_date,
                            ).format("LL")
                          : null}
                      </time>
                    </dd>
                  </div>
                </div>
                <div className="mt-2 sm:mt-0 sm:pl-4">
                  <dt className="inline text-gray-500">Frequency type: </dt>{" "}
                  <dd className="inline text-gray-700">
                    {invoiceDetail?.invoice_info.invoice_frequency}
                  </dd>
                </div>
                <div className="mt-6 border-t border-gray-900/5 pt-6 sm:pr-4">
                  <dt className="font-semibold text-gray-900">From</dt>
                  <dd className="mt-2 text-gray-500">
                    <span className="font-medium text-gray-900">
                      {invoiceDetail?.invoice_info?.invoice_from?.name}
                    </span>
                    <br />
                    {invoiceDetail?.invoice_info?.invoice_from?.email}
                    <br />
                    {formatPhoneNumber(
                      invoiceDetail?.invoice_info?.invoice_from?.phone,
                    )}
                  </dd>
                </div>
                <div className="mt-8 sm:mt-6 sm:border-t sm:border-gray-900/5 sm:pl-4 sm:pt-6">
                  <dt className="font-semibold text-gray-900">To</dt>
                  <dd className="mt-2 text-gray-500">
                    <span className="font-medium text-gray-900">
                      {addresseeNamePriority(invoiceDetail)}
                    </span>
                    {/* <br />
                    {addresseeBusinessAddr(invoiceDetail)}
                    <br />
                    {addresseeArea(invoiceDetail)} */}
                    <br />
                    {addresseeEmail(invoiceDetail)}
                    <br />
                    {formatPhoneNumber(addresseePhone(invoiceDetail))}
                    <br />
                  </dd>
                </div>
              </dl>
              <InvoiceDetailsTable invoice={invoiceDetail} />
            </div>
            <div className="lg:col-start-3">
              <InvoiceNotes invoice={invoiceDetail} />
            </div>
          </div>
        </div>
      </div>
    </PayPalScriptProvider>
  );
};

function InvoiceSummary({ invoice }) {
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const { cognitoId } = useAuth();
  const paymentMethodsQuery = useGetPaymentMethods();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const history = useHistory();

  let user_id = invoice?.invoice_info?.invoice_from?.user_id;
  let client_id = useAuth().cognitoId;
  const deluxePartnerId = invoice?.invoice_info?.metadata?.deluxe_partner_id;

  const handlePay = async (authContent, paymentType) => {
    const paymentMethodDetails = paymentMethodsQuery.data?.find(
      (i) => i.payment_method_id === selectedPaymentMethod,
    );

    const payload = {
      payment_type: paymentType,
      user_id,
      client_id,
      invoice_id: invoice?.invoice_id,
      payment_method_id: paymentMethodDetails.payment_method_id,
      auth_approved: true,
      auth_content: authContent,
    };

    const { data } = await api.post(`/platform/transactions/payments`, payload);
    return data;
  };

  return (
    <>
      <h2 className="sr-only">Summary</h2>
      <div className="rounded-lg bg-skin-primary shadow-sm ring-1 ring-gray-900/5">
        <dl className="flex flex-col flex-wrap">
          <div className="flex">
            <div className="flex-auto pl-6 pt-6">
              <dt className="text-sm font-semibold leading-6 text-skin-a11y">
                Amount
              </dt>
              <dd className="mt-1 text-xl font-bold leading-6 text-skin-a11y">
                $
                {convertThousandSeparator(
                  Number(invoice?.invoice_info?.invoice_total),
                )}
              </dd>
            </div>
            <div className="flex-none self-end px-6 pt-4">
              <dt className="sr-only">Status</dt>
              <dd className="rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-black ring-1 ring-inset ring-white">
                {(invoice?.invoice_info.invoice_status || "")
                  .split("_")
                  .map((word) => capitalizeFirstLetter(word))
                  .join(" ")}
              </dd>
            </div>
          </div>
          <div className="flex flex-col  justify-between lg:mt-6">
            <div className="mt-4 lg:mt-0 flex flex-none gap-x-4 border-t border-gray-900/5 px-6 lg:pt-0 pt-6">
              <dt className="flex-none">
                <span className="sr-only">Client</span>
                <UserCircle className="h-6 w-5 text-white" aria-hidden="true" />
              </dt>
              <dd className="text-sm font-medium leading-6 text-white">
                {invoice?.invoice_info.invoice_from?.name || ""}
              </dd>
            </div>
            <div className=" mt-4 flex  flex-none gap-x-4 px-6">
              <dt className="flex-none">
                <span className="sr-only">Due date</span>
                <CalendarDays
                  className="h-6 w-5 text-white"
                  aria-hidden="true"
                />
              </dt>
              <dd className="text-sm leading-6 text-white">
                <time>
                  {`Received on ${moment(
                    invoice?.invoice_info.invoice_send_date,
                  ).format("LL")}`}
                </time>
              </dd>
            </div>
          </div>
        </dl>
        <div
          className={cn(" border-slate-300/20 px-2 py-2", {
            "border-t mt-6": canInvoiceBePaid(invoice),
          })}
        >
          {canInvoiceBePaid(invoice) && (
            <>
              {showPaymentMethods ? (
                <PaymentMethodLists
                  paymentMethods={paymentMethodsQuery.data}
                  selectedPaymentMethod={selectedPaymentMethod}
                  setSelectedPaymentMethod={setSelectedPaymentMethod}
                  client_id={client_id}
                  user_id={user_id}
                  invoice_id={invoice?.invoice_id}
                  has_paypal={invoice?.invoice_info.invoice_add_paypal}
                  invoiceTotal={Number(invoice?.invoice_info?.invoice_total)}
                  deluxePartnerId={deluxePartnerId}
                  allowCreditCard={false /* !!invoice?.invoice_info?.allow_credit_card_payment */}
                />
              ) : (
                <button
                  className="text-sm font-semibold leading-6 text-skin-a11y px-4 py-3 hover:bg-slate-100/20 rounded-md w-full text-left transition-colors"
                  onClick={
                    !showPaymentMethods
                      ? () => setShowPaymentMethods(true)
                      : () => console.log(selectedPaymentMethod)
                  }
                >
                  Pay invoice <span aria-hidden="true">&rarr;</span>
                </button>
              )}
            </>
          )}
          {selectedPaymentMethod ? (
            <PayButton
              paymentList={paymentMethodsQuery.data || []}
              selectedPaymentMethod={selectedPaymentMethod}
              amount={invoice?.invoice_info?.invoice_total}
              handlePay={handlePay}
              selectedInvoice={invoice}
            />
          ) : null}
        </div>
        <div className=" border-t border-slate-300/20 px-6 py-2">
          <a
            href="https://pocketbookhelp.zendesk.com/hc/en-us/articles/24972239504407-Payment-Troubleshooting"
            target="_blank"
            className="text-sm font-medium leading-6 text-skin-a11y text-left transition-colors hover:border-b"
          >
            Need help?
          </a>
        </div>
      </div>
    </>
  );
}

const PayButton = ({
  paymentList,
  selectedPaymentMethod,
  handlePay,
  selectedInvoice,
}) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { cognitoId } = useAuth();
  const [isPaying, setIsPaying] = useState(false);
  const [displayPaymentModal, setDisplayPaymentModal] = useState(false);
  const [surcharge, setSurcharge] = useState(null);
  const [loadingSurcharge, setLoadingSurcharge] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const paymentMethodDetails = paymentList?.find(
    (i) => i.payment_method_id === selectedPaymentMethod,
  );
  const isCC = paymentMethodDetails?.account_type === "Credit Card";

  const invoiceTotal = Number(selectedInvoice?.invoice_info?.invoice_total);
  const clientName = selectedInvoice?.invoice_info?.invoice_from?.name || "";

  // Build a human-readable label for the selected payment method
  let paymentDisplayName = "";
  if (paymentMethodDetails) {
    if (isCC) {
      let cardType = "Credit Card";
      let maskedPan = "****";
      try {
        const jwtPayload = JSON.parse(
          atob(
            paymentMethodDetails.payment_method_data?.link_session_id?.split(
              ".",
            )[1],
          ),
        );
        cardType = jwtPayload.CardType || "Credit Card";
        maskedPan = jwtPayload.MaskedPan || "****";
      } catch {}
      paymentDisplayName = `${cardType} ****${maskedPan.slice(-4)}`;
    } else {
      const institution =
        paymentMethodDetails.payment_method_data?.institution?.name || "";
      const last4 =
        paymentMethodDetails.payment_method_data?.ach?.account_number ||
        paymentMethodDetails.payment_method_data?.account?.mask ||
        "";
      paymentDisplayName = `${institution} account ending in ${last4}`;
    }
  }

  const totalAmount = isCC && surcharge ? surcharge.total_amount : invoiceTotal;
  const formattedTotal = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(totalAmount);
  const formattedInvoiceTotal = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(invoiceTotal);

  const authContent = `By clicking "Submit Payment" I authorize ${clientName} to charge my ${paymentDisplayName} for a one-time payment of ${formattedTotal}.`;

  const openModal = async () => {
    setPaymentError(null);
    setDisplayPaymentModal(true);
    if (isCC) {
      setLoadingSurcharge(true);
      try {
        const surchargeData = await getDeluxeSurcharge({
          user_id: selectedInvoice?.invoice_info?.invoice_from?.user_id,
          amount: invoiceTotal,
          payment_method_id: selectedPaymentMethod,
        });
        setSurcharge(surchargeData);
      } catch {
        toast.error("Failed to calculate card fees. Please try again.");
        setDisplayPaymentModal(false);
      } finally {
        setLoadingSurcharge(false);
      }
    }
  };

  const handlePayment = async () => {
    setIsPaying(true);
    try {
      const result = await handlePay(authContent, isCC ? "credit_card" : "ach");
      if (!result || result.error) {
        throw new Error(result?.error_description || "Payment failed. Please try again.");
      }
      setIsPaying(false);
      setDisplayPaymentModal(false);
      toast.success("Payment was made", { autoClose: 5000 });
      setTimeout(() => {
        queryClient.invalidateQueries("invoices");
        history.push("/invoices");
      }, 3500);
    } catch (error) {
      setIsPaying(false);
      setPaymentError(error?.message || "Payment failed. Please try again.");
      console.error("Payment failed:", error);
    }
  };

  const PayIcon = isCC ? CreditCard : Building2;

  return (
    <>
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.15)",
          margin: "0 16px 12px",
          paddingTop: 12,
        }}
      />
      <div style={{ padding: "0 16px 12px" }}>
        <button
          onClick={openModal}
          disabled={isPaying}
          className="w-full"
          style={{
            background: "rgba(255,255,255,0.95)",
            border: "none",
            borderRadius: 12,
            padding: "14px 16px",
            cursor: isPaying ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "opacity 0.2s, background-color 0.2s",
            opacity: isPaying ? 0.6 : 1,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "rgba(0,0,0,0.08)",
                position: "absolute",
              }}
            />
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                position: "relative",
              }}
            >
              <PayIcon size={15} style={{ color: "#111827" }} />
            </div>
            <div style={{ textAlign: "left" }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#6b7280",
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                Pay with
              </div>
              <div
                style={{ fontSize: 13, fontWeight: 600, marginTop: 3, color: "#111827" }}
              >
                {paymentDisplayName}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}
            >
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(invoiceTotal)}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              style={{ width: 16, height: 16, opacity: 0.4, fill: "#111827" }}
            >
              <path
                fillRule="evenodd"
                d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </button>
      </div>
      <Modal
        open={displayPaymentModal}
        onClose={() => {
          if (!isPaying) { setDisplayPaymentModal(false); setPaymentError(null); }
        }}
        aria-labelledby="modal-confirm-payment"
      >
        <Box sx={style}>
          {paymentError ? (
            <div style={{ padding: "8px 0" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 12,
                  padding: "24px 0",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    background: "#fee2e2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ width: 26, height: 26, color: "#dc2626" }}
                    aria-hidden="true"
                    focusable="false"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#111827", marginBottom: 4 }}>
                    Payment failed
                  </div>
                  <div style={{ fontSize: 13, color: "#6b7280" }}>{paymentError}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <ButtonTw
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setPaymentError(null); setDisplayPaymentModal(false); }}
                >
                  Close
                </ButtonTw>
                <ButtonTw
                  variant="green"
                  className="flex-1"
                  onClick={() => setPaymentError(null)}
                >
                  Try Again
                </ButtonTw>
              </div>
            </div>
          ) : loadingSurcharge ? (
            <div style={{ padding: "8px 0" }}>
              {/* Shimmer amount */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  paddingBottom: 24,
                }}
              >
                <div
                  style={{
                    width: 120,
                    height: 12,
                    borderRadius: 6,
                    background:
                      "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.4s infinite",
                  }}
                />
                <div
                  style={{
                    width: 160,
                    height: 36,
                    borderRadius: 8,
                    background:
                      "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.4s infinite",
                  }}
                />
              </div>
              {/* Shimmer rows */}
              <div
                style={{
                  background: "#f9fafb",
                  borderRadius: 10,
                  overflow: "hidden",
                }}
              >
                {[100, 130, 160, 100, 120].map((w, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "12px 16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: i < 4 ? "1px solid #f3f4f6" : undefined,
                    }}
                  >
                    <div
                      style={{
                        width: 60,
                        height: 10,
                        borderRadius: 5,
                        background:
                          "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.4s infinite",
                      }}
                    />
                    <div
                      style={{
                        width: w,
                        height: 10,
                        borderRadius: 5,
                        background:
                          "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.4s infinite",
                      }}
                    />
                  </div>
                ))}
              </div>
              <style>{`@keyframes shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }`}</style>
            </div>
          ) : (
            <>
              {/* Amount hero */}
              <div style={{ textAlign: "center", padding: "8px 0 20px" }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#9ca3af",
                    marginBottom: 4,
                  }}
                >
                  Total due to {clientName}
                </div>
                <div
                  style={{
                    fontSize: 36,
                    fontWeight: 700,
                    color: "#111827",
                    letterSpacing: "-0.5px",
                  }}
                >
                  {formattedTotal}
                </div>
              </div>

              {isCC ? (
                <>
                  {/* CC payment details */}
                  <div
                    style={{
                      background: "#f9fafb",
                      borderRadius: 10,
                      overflow: "hidden",
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: "1px solid #f3f4f6",
                      }}
                    >
                      <span style={{ fontSize: 13, color: "#6b7280" }}>
                        Card
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#111827",
                        }}
                      >
                        {paymentDisplayName}
                      </span>
                    </div>
                    {paymentMethodDetails?.payment_method_data
                      ?.name_on_account && (
                      <div
                        style={{
                          padding: "10px 16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        <span style={{ fontSize: 13, color: "#6b7280" }}>
                          Cardholder
                        </span>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#111827",
                          }}
                        >
                          {
                            paymentMethodDetails.payment_method_data
                              .name_on_account
                          }
                        </span>
                      </div>
                    )}
                    <div
                      style={{
                        padding: "10px 16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: surcharge
                          ? "1px solid #f3f4f6"
                          : undefined,
                      }}
                    >
                      <span style={{ fontSize: 13, color: "#6b7280" }}>
                        Email
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#111827",
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {addresseeEmail(selectedInvoice)}
                      </span>
                    </div>
                    {surcharge && (
                      <>
                        {totalAmount !== invoiceTotal && (
                          <>
                            <div
                              style={{
                                padding: "10px 16px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderBottom: "1px solid #f3f4f6",
                              }}
                            >
                              <span style={{ fontSize: 13, color: "#6b7280" }}>
                                Invoice Total
                              </span>
                              <span style={{ fontSize: 13, color: "#111827" }}>
                                {formattedInvoiceTotal}
                              </span>
                            </div>
                            <div
                              style={{
                                padding: "10px 16px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderBottom: "1px solid #f3f4f6",
                              }}
                            >
                              <span style={{ fontSize: 13, color: "#6b7280" }}>
                                Surcharge Fee
                              </span>
                              <span style={{ fontSize: 13, color: "#111827" }}>
                                {new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                }).format(surcharge.surcharge_amount)}
                              </span>
                            </div>
                          </>
                        )}
                        <div
                          style={{
                            padding: "10px 16px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#111827",
                            }}
                          >
                            Payment Total
                          </span>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#111827",
                            }}
                          >
                            {formattedTotal}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "flex-start",
                      marginBottom: 20,
                      padding: "0 2px",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      style={{
                        width: 13,
                        height: 13,
                        color: "#374151",
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    >
                      <rect x="2.5" y="7" width="11" height="7.5" rx="1.5" />
                      <path d="M5 7V5a3 3 0 0 1 6 0v2" />
                    </svg>
                    <span
                      style={{
                        fontSize: 11,
                        color: "#374151",
                        lineHeight: 1.5,
                      }}
                    >
                      {authContent}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  {/* ACH transfer summary */}
                  <div
                    style={{
                      background: "#f9fafb",
                      borderRadius: 10,
                      padding: "14px 16px",
                      marginBottom: 14,
                      fontSize: 13,
                      color: "#374151",
                      lineHeight: 1.6,
                    }}
                  >
                    {formattedInvoiceTotal} will be transferred from{" "}
                    <strong>
                      {
                        paymentMethodDetails?.payment_method_data?.institution
                          ?.name
                      }{" "}
                      {capitalizeFirstLetter(
                        paymentMethodDetails?.payment_method_data?.ach
                          ?.account_type ||
                          paymentMethodDetails?.payment_method_data?.account
                            ?.subtype ||
                          "",
                      )}{" "}
                      account ending in *
                      {paymentMethodDetails?.payment_method_data?.ach
                        ?.account_number ||
                        paymentMethodDetails?.payment_method_data?.account
                          ?.mask}
                    </strong>{" "}
                    to <strong>{clientName}</strong> on{" "}
                    <strong>{moment().format("MM/DD/YYYY")}</strong>.
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "flex-start",
                      marginBottom: 20,
                      padding: "0 2px",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      style={{
                        width: 13,
                        height: 13,
                        color: "#374151",
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                    >
                      <rect x="2.5" y="7" width="11" height="7.5" rx="1.5" />
                      <path d="M5 7V5a3 3 0 0 1 6 0v2" />
                    </svg>
                    <span
                      style={{
                        fontSize: 11,
                        color: "#374151",
                        lineHeight: 1.5,
                      }}
                    >
                      By clicking "Submit Payment" you authorize a one-time
                      debit of {formattedInvoiceTotal} from your designated
                      account to {clientName} on {moment().format("MM/DD/YYYY")}
                      . Your authorization is effective immediately and the
                      transfer will be processed today or on the next business
                      day if we receive your request after our ACH processing
                      deadline.
                    </span>
                  </div>
                </>
              )}

              <div style={{ display: "flex", gap: 12 }}>
                <ButtonTw
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDisplayPaymentModal(false)}
                  disabled={isPaying}
                >
                  Cancel
                </ButtonTw>
                <ButtonTw
                  variant="green"
                  className="flex-1"
                  onClick={handlePayment}
                  disabled={isPaying}
                >
                  {isPaying ? "Processing..." : "Submit Payment"}
                </ButtonTw>
              </div>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

const PaymentMethodSelect = ({
  type,
  institution,
  last4,
  isCC,
  setSelectedPaymentMethod,
  selectedPaymentMethod,
  paymentMethodId,
}) => {
  const handleClick = () => {
    setSelectedPaymentMethod(paymentMethodId);
  };

  const isSelected = selectedPaymentMethod === paymentMethodId;
  const Icon = isCC ? CreditCard : Building2;

  return (
    <li
      onClick={handleClick}
      className="hover:bg-skin-a11y/10"
      style={{
        listStyle: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px 14px",
        marginBottom: 8,
        borderRadius: 10,
        cursor: "pointer",
        background: isSelected
          ? "rgba(255,255,255,0.15)"
          : "rgba(255,255,255,0.07)",
        border: `1px solid ${
          isSelected ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.12)"
        }`,
        transition: "background 0.2s, border-color 0.2s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 8,
            background: "rgba(255,255,255,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={16} className="text-skin-a11y" />
        </div>
        <div>
          <div
            className="text-skin-a11y"
            style={{ fontSize: 13, fontWeight: 600 }}
          >
            {institution}
          </div>
          <div
            className="text-skin-a11y"
            style={{ fontSize: 11, opacity: 0.6 }}
          >
            {type
              ? `${capitalizeFirstLetter(type)} ****${last4}`
              : `****${last4}`}
          </div>
        </div>
      </div>
      {isSelected && (
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#22c55e",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <svg
            viewBox="0 0 12 12"
            fill="none"
            style={{ width: 10, height: 10 }}
          >
            <path
              d="M2 6l3 3 5-5"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </li>
  );
};

const ConnectPlaidButton = styled.button`
  border: 0;
  background-color: black;
  color: white;
  font-weight: bold;
  padding: 1rem;
  :hover {
    background-color: white;
    color: black;
  }
`;

const PaymentMethodLists = ({
  paymentMethods,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  user_id,
  client_id,
  has_paypal,
  invoice_id,
  invoiceTotal,
  deluxePartnerId,
  allowCreditCard,
}) => {
  const { cognitoId } = useAuth();
  const queryClient = useQueryClient();

  const deluxeMerchantId = deluxePartnerId;

  const [initiateOpen, setInitiateOpen] = useState(false);
  const [initiateData, setInitiateData] = useState();
  const [openPlaid, setOpenPlaid] = useState(false);
  const [showCCModal, setShowCCModal] = useState(false);
  const [isLinkingCard, setIsLinkingCard] = useState(false);

  const handleCCSuccess = async (response) => {
    setShowCCModal(false);
    setIsLinkingCard(true);
    try {
      const result = await setDeluxePaymentToken({
        user_id: cognitoId,
        token: response.Token,
        jwt: response.jwt,
        name_on_card: response.NameOnCard || undefined,
        expiration_month_year: response.ExpMonthYear || undefined,
        account_type: "Credit Card",
        payment_method_default: false,
      });
      await queryClient.invalidateQueries({ queryKey: ["payment_methods"] });
      setSelectedPaymentMethod(result.payment_method_id);
    } catch {
      toast.error("Failed to save card. Please try again.");
    } finally {
      setIsLinkingCard(false);
    }
  };

  return (
    <div className="w-full mb-4 px-4">
      <h3 className="text-sm font-semibold leading-6 text-skin-a11y mb-4">
        Select a payment method
      </h3>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {(paymentMethods || []).map((paymentMethod) => {
          if (paymentMethod.payment_method_status !== "active") return null;

          const isCC = paymentMethod.account_type === "Credit Card";
          if (isCC && !allowCreditCard) return null;

          let institution, type, last4;
          if (isCC) {
            let cardType = "Credit Card";
            let maskedPan = "****";
            try {
              const jwtPayload = JSON.parse(
                atob(
                  paymentMethod.payment_method_data?.link_session_id?.split(
                    ".",
                  )[1],
                ),
              );
              cardType = jwtPayload.CardType || "Credit Card";
              maskedPan = jwtPayload.MaskedPan || "****";
            } catch {}
            institution = cardType;
            type = "";
            last4 = maskedPan.slice(-4);
          } else {
            institution =
              paymentMethod.payment_method_data?.institution?.name || "";
            type =
              paymentMethod.payment_method_data?.ach?.account_type ||
              paymentMethod.payment_method_data?.account?.subtype;
            last4 =
              paymentMethod.payment_method_data?.ach?.account_number ||
              paymentMethod.payment_method_data?.account?.mask;
          }

          return (
            <PaymentMethodSelect
              key={paymentMethod.payment_method_id}
              institution={institution}
              type={type}
              last4={last4}
              isCC={isCC}
              setSelectedPaymentMethod={setSelectedPaymentMethod}
              selectedPaymentMethod={selectedPaymentMethod}
              paymentMethodId={paymentMethod.payment_method_id}
            />
          );
        })}
      </ul>

      <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.2)" }} />

      <div className="flex flex-col 2xl:flex-row gap-2">
        <button
          type="button"
          onClick={() => setInitiateOpen(true)}
          className="w-full transition-colors hover:bg-skin-a11y/10"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            background: "rgba(255,255,255,0.07)",
            border: "1px dashed rgba(255,255,255,0.25)",
            borderRadius: 10,
            cursor: "pointer",
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "rgba(255,255,255,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              position: "relative",
            }}
          >
            <Building2 size={15} className="text-skin-a11y" />
            <div
              style={{
                position: "absolute",
                top: -5,
                right: -5,
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Plus size={9} className="text-skin-a11y" />
            </div>
          </div>
          <div style={{ textAlign: "left" }}>
            <div
              className="text-skin-a11y"
              style={{ fontSize: 13, fontWeight: 600 }}
            >
              Link Bank Account
            </div>
          </div>
        </button>
        {deluxeMerchantId && allowCreditCard && (
          <button
            type="button"
            onClick={() => setShowCCModal(true)}
            disabled={isLinkingCard}
            className="w-full transition-colors hover:bg-skin-a11y/10"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 14px",
              background: "rgba(255,255,255,0.07)",
              border: "1px dashed rgba(255,255,255,0.25)",
              borderRadius: 10,
              cursor: isLinkingCard ? "not-allowed" : "pointer",
              opacity: isLinkingCard ? 0.6 : 1,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "rgba(255,255,255,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                position: "relative",
              }}
            >
              <CreditCard size={15} className="text-skin-a11y" />
              <div
                style={{
                  position: "absolute",
                  top: -5,
                  right: -5,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Plus size={9} className="text-skin-a11y" />
              </div>
            </div>
            <div style={{ textAlign: "left" }}>
              <div
                className="text-skin-a11y"
                style={{ fontSize: 13, fontWeight: 600 }}
              >
                {isLinkingCard ? "Saving card..." : "Add Card"}
              </div>
            </div>
          </button>
        )}
      </div>

      <InitiateLinkAccount
        open={initiateOpen}
        onOpenChange={setInitiateOpen}
        onSubmit={(values) => {
          setInitiateData(values);
          setOpenPlaid(true);
        }}
      />
      {openPlaid && initiateData ? (
        <Plaid
          open={openPlaid}
          onClose={() => setOpenPlaid(false)}
          method_type={"PLAID"}
          linkData={initiateData}
          payment_method_status={""}
        />
      ) : null}

      {showCCModal && (
        <Modal
          open={showCCModal}
          onClose={() => setShowCCModal(false)}
          aria-labelledby="modal-add-card"
        >
          <Box sx={{ ...style, width: 480 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Title>Add Card</Title>
              <button
                onClick={() => setShowCCModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                  color: "#6b7280",
                  fontSize: 18,
                }}
              >
                ✕
              </button>
            </div>
            <EmbeddedPaymentForm
              amount={invoiceTotal}
              merchantId={deluxeMerchantId}
              onSuccess={handleCCSuccess}
              onError={(err) => toast.error(err)}
              onCancel={() => setShowCCModal(false)}
            />
          </Box>
        </Modal>
      )}
    </div>
  );
};

function InvoiceDetailsTable({ invoice }) {
  return (
    <table className="mt-16 w-full whitespace-nowrap text-left text-sm leading-6">
      <colgroup>
        <col className="w-full" />
        <col />
        <col />
        <col />
      </colgroup>
      <thead className="border-b border-gray-200 text-gray-900">
        <tr>
          <th scope="col" className="px-0 py-3 font-semibold">
            Line Items
          </th>
          <th
            scope="col"
            className="hidden py-3 pl-8 pr-0 text-right font-semibold sm:table-cell"
          >
            Units
          </th>
          <th
            scope="col"
            className="hidden py-3 pl-8 pr-0 text-right font-semibold sm:table-cell"
          >
            Rate
          </th>
          <th scope="col" className="py-3 pl-8 pr-0 text-right font-semibold">
            Price
          </th>
        </tr>
      </thead>
      <tbody>
        {(invoice?.invoice_info.invoice_line_items || []).map((item, i) => (
          <tr key={i} className="border-b border-gray-100">
            <td className="max-w-0 px-0 py-5 align-top">
              <div className="truncate font-medium text-gray-900">
                {item.description}
              </div>
              {/* <div className="truncate text-gray-500">
                      {item.description}
                    </div> */}
            </td>
            <td className="hidden py-5 pl-8 pr-0 text-right align-top tabular-nums text-gray-700 sm:table-cell">
              {item.quantity}
            </td>
            <td className="hidden py-5 pl-8 pr-0 text-right align-top tabular-nums text-gray-700 sm:table-cell">
              ${convertThousandSeparator(Number(item.unit_price))}
            </td>
            <td className="py-5 pl-8 pr-0 text-right align-top tabular-nums text-gray-700">
              ${convertThousandSeparator(Number(item.item_total))}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <th
            scope="row"
            className="px-0 pb-0 pt-6 font-normal text-gray-700 sm:hidden"
          >
            Subtotal
          </th>
          <th
            scope="row"
            colSpan={3}
            className="hidden px-0 pb-0 pt-6 text-right font-normal text-gray-700 sm:table-cell"
          >
            Subtotal
          </th>
          <td className="pb-0 pl-8 pr-0 pt-6 text-right tabular-nums text-gray-900">
            $
            {convertThousandSeparator(
              Number(invoice?.invoice_info?.invoice_subtotal),
            )}
          </td>
        </tr>
        {invoice?.invoice_info.invoice_shipping_fee ? (
          <tr>
            <th
              scope="row"
              className="pt-4 font-normal text-gray-700 sm:hidden"
            >
              Shipping fee
            </th>
            <th
              scope="row"
              colSpan={3}
              className="hidden pt-4 text-right font-normal text-gray-700 sm:table-cell"
            >
              Shipping fee
            </th>
            <td className="pb-0 pl-8 pr-0 pt-4 text-right tabular-nums text-gray-900">
              $
              {convertThousandSeparator(
                Number(invoice?.invoice_info.invoice_shipping_fee),
              )}
            </td>
          </tr>
        ) : null}
        <tr>
          <th scope="row" className="pt-4 font-normal text-gray-700 sm:hidden">
            Tax
          </th>
          <th
            scope="row"
            colSpan={3}
            className="hidden pt-4 text-right font-normal text-gray-700 sm:table-cell"
          >
            Tax
          </th>
          <td className="pb-0 pl-8 pr-0 pt-4 text-right tabular-nums text-gray-900">
            $
            {convertThousandSeparator(
              Number(invoice?.invoice_info.invoice_tax_amount),
            )}
          </td>
        </tr>
        {invoice?.invoice_info.invoice_pass_processing_fee && (
          <tr>
            <th
              scope="row"
              className="pt-4 font-normal text-gray-700 sm:hidden"
            >
              Fee
            </th>
            <th
              scope="row"
              colSpan={3}
              className="hidden pt-4 text-right font-normal text-gray-700 sm:table-cell"
            >
              Fee
            </th>
            <td className="pb-0 pl-8 pr-0 pt-4 text-right tabular-nums text-gray-900">
              $
              {convertThousandSeparator(
                Number(invoice?.invoice_info.invoice_processing_fee),
              )}
            </td>
          </tr>
        )}
        {!!invoice?.invoice_info?.provider_surcharge_amount &&
          invoice?.invoice_info?.invoice_payment_method_type?.toLowerCase() === "credit_card" && (
          <tr>
            <th
              scope="row"
              className="pt-4 font-normal text-gray-700 sm:hidden"
            >
              Surcharge Fee
            </th>
            <th
              scope="row"
              colSpan={3}
              className="hidden pt-4 text-right font-normal text-gray-700 sm:table-cell"
            >
              Surcharge Fee
            </th>
            <td className="pb-0 pl-8 pr-0 pt-4 text-right tabular-nums text-gray-900">
              $
              {convertThousandSeparator(
                Number(invoice?.invoice_info.provider_surcharge_amount),
              )}
            </td>
          </tr>
        )}
        <tr>
          <th
            scope="row"
            className="pt-4 font-semibold text-gray-900 sm:hidden"
          >
            Total
          </th>
          <th
            scope="row"
            colSpan={3}
            className="hidden pt-4 text-right font-semibold text-gray-900 sm:table-cell"
          >
            Total
          </th>
          <td className="pb-0 pl-8 pr-0 pt-4 text-right font-semibold tabular-nums text-gray-900">
            $
            {convertThousandSeparator(
              Number(invoice?.invoice_info?.invoice_total),
            )}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

function InvoiceNotes({ invoice }) {
  return (
    <div className="overflow-hidden bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6 space-y-2">
        <h2 className="text-sm font-semibold leading-6 text-gray-900">Notes</h2>
        {invoice?.invoice_info.invoice_notes ? (
          <p className="text-sm text-slate-700">
            {invoice?.invoice_info.invoice_notes}
          </p>
        ) : (
          <p className="text-sm text-slate-700">
            There are no notes for this invoice
          </p>
        )}
      </div>
    </div>
  );
}

const Title = styled.div`
  ${textStyles.title.h3};
`;

const Text = styled.div`
  ${textStyles.body.b2};
  margin-bottom: 32px;
`;

const BottomText = styled.div`
  ${textStyles.body.b2}
`;

export default CustomerInvoiceDetail;
